import { addDays, addMonths, partsOf } from './dates.js';

/* ------------------------------------------------------------------------- *
 * Vários dias, ou vários meses, avulsos e em faixa — a conta sem React
 *
 * ## O valor é uma lista de TEXTO, e cada item é um de dois formatos
 *
 * - `2026-03` (ou `2026-03-15`): uma unidade solta;
 * - `2026-01/2026-06` (ou `2026-01-10/2026-01-20`): uma faixa FECHADA nos dois
 *   lados — a barra é a do intervalo da ISO 8601.
 *
 * Lista de texto porque é o que `FilterValues` já carrega e o que viaja num
 * endereço sem codificação nenhuma; e ISO porque quem vai ler isto do outro
 * lado é uma rota com `zod`, e `AAAA-MM` é exatamente o `periodSchema` que as
 * onze telas de competência já mandam hoje.
 *
 * ## A forma é CANÔNICA, e é a decisão menos óbvia daqui
 *
 * Toda operação devolve a lista ordenada, sem sobreposição e sem vizinhas:
 * marcar jan, depois fev, depois mar dá `['2026-01/2026-03']`, e não três
 * itens. É o mesmo motivo do `MultiSelect` sair na ordem das opções e não na
 * do clique — a chave de cache da consulta não pode distinguir duas listas que
 * pedem a mesma coisa. Aqui o risco é maior que ordem: `jan, fev` e `jan/fev`
 * são o MESMO conjunto escrito de dois jeitos, e uma rota que somasse os itens
 * em vez de unir contaria fevereiro duas vezes se a lista viesse sobreposta.
 *
 * O preço é que a tela não lembra se a pessoa marcou três meses um a um ou os
 * escolheu como faixa. Não há pergunta de negócio que dependa disso.
 * ------------------------------------------------------------------------- */

/** O grão do calendário: dia (`AAAA-MM-DD`) ou mês (`AAAA-MM`). */
export type CalendarUnit = 'day' | 'month';

/** Uma faixa fechada; `from === to` é a unidade solta. */
export interface CalendarSpan {
  from: string;
  to: string;
}

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

/** É uma unidade válida do grão? `2026-02-30` e `2026-13` não são. */
export function isUnit(value: string, unit: CalendarUnit): boolean {
  return unit === 'month' ? MONTH.test(value) : partsOf(value) !== null;
}

/** A unidade seguinte — o que decide se duas faixas são vizinhas. */
export function nextUnit(value: string, unit: CalendarUnit): string {
  return unit === 'month' ? addMonths(`${value}-01`, 1).slice(0, 7) : addDays(value, 1);
}

/** A anterior. */
export function previousUnit(value: string, unit: CalendarUnit): string {
  return unit === 'month' ? addMonths(`${value}-01`, -1).slice(0, 7) : addDays(value, -1);
}

/**
 * O texto → faixas, já canônicas.
 *
 * O que não se lê é DESCARTADO, e não vira erro: o valor vem de um endereço
 * que qualquer pessoa edita, e o seletor precisa abrir mesmo assim. Faixa
 * escrita de trás para a frente (`2026-06/2026-01`) é desvirada — a intenção
 * não é ambígua.
 */
export function parseSelection(
  values: readonly string[],
  unit: CalendarUnit,
): CalendarSpan[] {
  const spans: CalendarSpan[] = [];
  for (const raw of values) {
    const [a, b, extra] = raw.trim().split('/');
    if (extra !== undefined || a === undefined) continue;
    const from = a;
    const to = b ?? a;
    if (!isUnit(from, unit) || !isUnit(to, unit)) continue;
    spans.push(from <= to ? { from, to } : { from: to, to: from });
  }
  return merge(spans, unit);
}

/** Faixas → texto. Unidade solta sai sem a barra. */
export function formatSelection(spans: readonly CalendarSpan[]): string[] {
  return spans.map((s) => (s.from === s.to ? s.from : `${s.from}/${s.to}`));
}

/**
 * Ordena e funde o que se toca. As unidades são ISO de largura fixa, então a
 * ordem do texto É a ordem do calendário — sem `Date` nenhum.
 */
function merge(spans: readonly CalendarSpan[], unit: CalendarUnit): CalendarSpan[] {
  const sorted = [...spans].sort((x, y) =>
    x.from < y.from ? -1 : x.from > y.from ? 1 : 0,
  );
  const out: CalendarSpan[] = [];
  for (const span of sorted) {
    const last = out[out.length - 1];
    if (last && span.from <= nextUnit(last.to, unit)) {
      if (span.to > last.to) last.to = span.to;
    } else {
      out.push({ ...span });
    }
  }
  return out;
}

/** A unidade está dentro de alguma faixa? */
export function isSelected(
  values: readonly string[],
  value: string,
  unit: CalendarUnit,
): boolean {
  return parseSelection(values, unit).some((s) => s.from <= value && value <= s.to);
}

/**
 * Marca a unidade solta, ou a desmarca se já estava — inclusive quando ela
 * está NO MEIO de uma faixa: desmarcar março em jan/jun deixa jan/fev e
 * abr/jun. É o que a pessoa vê no calendário (o março apagando), e o
 * contrário — desmarcar apagar a faixa inteira — tiraria cinco meses com um
 * clique que mirou um.
 */
export function toggleUnit(
  values: readonly string[],
  value: string,
  unit: CalendarUnit,
): string[] {
  if (!isUnit(value, unit)) return formatSelection(parseSelection(values, unit));
  const spans = parseSelection(values, unit);
  const hit = spans.find((s) => s.from <= value && value <= s.to);
  if (!hit) return formatSelection(merge([...spans, { from: value, to: value }], unit));

  const rest = spans.filter((s) => s !== hit);
  if (hit.from < value) rest.push({ from: hit.from, to: previousUnit(value, unit) });
  if (value < hit.to) rest.push({ from: nextUnit(value, unit), to: hit.to });
  return formatSelection(merge(rest, unit));
}

/** Acrescenta a faixa entre as duas pontas, em qualquer ordem. */
export function addSpan(
  values: readonly string[],
  a: string,
  b: string,
  unit: CalendarUnit,
): string[] {
  const spans = parseSelection(values, unit);
  if (!isUnit(a, unit) || !isUnit(b, unit)) return formatSelection(spans);
  const span = a <= b ? { from: a, to: b } : { from: b, to: a };
  return formatSelection(merge([...spans, span], unit));
}

/** Tira um item da lista canônica — o "×" de cada linha do seletor. */
export function removeSpan(
  values: readonly string[],
  span: CalendarSpan,
  unit: CalendarUnit,
): string[] {
  return formatSelection(
    parseSelection(values, unit).filter((s) => s.from !== span.from || s.to !== span.to),
  );
}

/* ------------------------------------------------------------------------- *
 * Os rótulos
 * ------------------------------------------------------------------------- */

const SHORT_MONTHS = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
] as const;

/** `2026-03` → `mar/2026`; `2026-03-15` → `15/03/2026`. */
export function unitLabel(value: string, unit: CalendarUnit): string {
  if (unit === 'month') {
    return `${SHORT_MONTHS[Number(value.slice(5, 7)) - 1]}/${value.slice(0, 4)}`;
  }
  return `${value.slice(8, 10)}/${value.slice(5, 7)}/${value.slice(0, 4)}`;
}

/** Uma linha: `mar/2026`, ou `jan/2026 a jun/2026`. */
export function spanLabel(span: CalendarSpan, unit: CalendarUnit): string {
  return span.from === span.to
    ? unitLabel(span.from, unit)
    : `${unitLabel(span.from, unit)} a ${unitLabel(span.to, unit)}`;
}

/** Quantas unidades a seleção cobre — o que o gatilho conta. */
export function countUnits(values: readonly string[], unit: CalendarUnit): number {
  let total = 0;
  for (const span of parseSelection(values, unit)) {
    if (unit === 'month') {
      const [fy, fm] = span.from.split('-').map(Number) as [number, number];
      const [ty, tm] = span.to.split('-').map(Number) as [number, number];
      total += (ty - fy) * 12 + (tm - fm) + 1;
    } else {
      const ms =
        Date.parse(`${span.to}T00:00:00Z`) - Date.parse(`${span.from}T00:00:00Z`);
      total += Math.round(ms / 86_400_000) + 1;
    }
  }
  return total;
}

/**
 * O que o gatilho escreve.
 *
 * Um item cabe por extenso (`jan/2026 a jun/2026`); mais de um vira contagem
 * de UNIDADES, e não de itens — "3 selecionados" sobre `jan/jun, set` diria
 * dois quando a pessoa escolheu sete meses. É a mesma recusa do `MultiSelect`
 * em listar tudo no gatilho, e pelo mesmo motivo: o painel de filtros tem
 * trilhas de altura fixa.
 */
export function selectionSummary(
  values: readonly string[],
  unit: CalendarUnit,
  placeholder: string,
): string {
  const spans = parseSelection(values, unit);
  if (spans.length === 0) return placeholder;
  if (spans.length === 1) return spanLabel(spans[0]!, unit);
  const n = countUnits(values, unit);
  return unit === 'month' ? `${n} meses` : `${n} dias`;
}
